import {DefinitionReadOnlyOverview} from '@github-ui/custom-properties-core/components/DefinitionReadOnlyOverview'
import {PropertiesBreadcrumbsPageHeader} from '@github-ui/custom-properties-core/components/PropertiesBreadcrumbsPageHeader'
import {PropertyManagementBanner} from '@github-ui/custom-properties-core/components/PropertyManagementBanner'
import {ServerErrorFormBanner as SharedServerErrorFormBanner} from '@github-ui/custom-properties-core/components/ServerErrorFormBanner'
import type {CustomPropertyDetailsPagePayload} from '@github-ui/custom-properties-types'
import {propertyDefinitionSettingsPath} from '@github-ui/paths'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useNavigate} from '@github-ui/use-navigate'
import {PencilIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useEffect, useRef, useState} from 'react'

import {DefinitionDangerZone} from '../components/DefinitionDangerZone'
import {DefinitionForm} from '../components/DefinitionForm'
import {useListPropertiesPath} from '../hooks/use-properties-paths'
import {useEditPropertyPathBuilder} from '../hooks/use-property-details-paths'
import {usePropertySource} from '../hooks/use-property-source'
import styles from './CustomPropertyDetailsPage.module.css'

export function CustomPropertyDetailsPage() {
  const {propertyNames, definition, propertiesLimits, business, canManageProperty, orgConflicts} =
    useRoutePayload<CustomPropertyDetailsPagePayload>()
  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string */
  const [formError, setFormError] = useState<SafeHTMLString>('' as SafeHTMLString)
  const {settingsLevel} = usePropertySource()
  const [isEditing, setEditing] = useState(!definition)
  const canEdit = !!definition && settingsLevel === definition.source.type

  const navigate = useNavigate()
  const listPropertiesPath = useListPropertiesPath()
  const propertyDetailsPathBuilder = useEditPropertyPathBuilder()

  function returnToList() {
    navigate(listPropertiesPath)
  }

  function onSuccess(propertyName: string) {
    setEditing(false)
    navigate(propertyDetailsPathBuilder(propertyName))
  }

  function onCancel() {
    const isEditingExistingDefinition = !!definition
    if (isEditingExistingDefinition) {
      setEditing(false)
    } else {
      returnToList()
    }
  }

  const isPropNotManagedByThisContext = definition && definition.source.type !== settingsLevel
  const canDeleteProperty = settingsLevel === definition?.source?.type
  const canPromoteProperty = settingsLevel === 'business' && definition?.source.type === 'org'
  const showDangerZone = canDeleteProperty || canPromoteProperty

  const serverErrorBannerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    serverErrorBannerRef.current?.focus()
  }, [formError, serverErrorBannerRef])

  const requireExplicitPropertyValuesEnabled = useFeatureFlag('require_explicit_property_values')

  return (
    <>
      {formError && (
        <div className={styles.errorContainer}>
          <SharedServerErrorFormBanner ref={serverErrorBannerRef}>
            <VerifiedHTMLText html={formError} />
          </SharedServerErrorFormBanner>
        </div>
      )}
      <PropertiesBreadcrumbsPageHeader
        className="pr-mb-3"
        pageTitle={definition?.propertyName || 'New property'}
        breadcrumbLinkProps={{to: listPropertiesPath}}
        actions={
          !isEditing &&
          canEdit && (
            <Button className="pr-ml-3 f5 text-semibold" onClick={() => setEditing(true)} leadingVisual={PencilIcon}>
              Edit
            </Button>
          )
        }
      />
      {isPropNotManagedByThisContext && (
        <PropertyManagementBanner
          className="pr-mb-3 mt-2"
          definitionSource={definition.source}
          definitionDetailsPath={propertyDefinitionSettingsPath({
            pathPrefix: definition.source.type === 'business' ? 'enterprises' : 'organizations',
            sourceName: definition.source.slug,
            propertyName: definition.propertyName,
          })}
          viewerCanManage={canManageProperty}
        />
      )}
      {definition && !isEditing ? (
        <div className="pr-mt-3">
          <DefinitionReadOnlyOverview>
            <DefinitionReadOnlyOverview.CommonAttributes definition={definition} />
            {requireExplicitPropertyValuesEnabled && definition.required && (
              <DefinitionReadOnlyOverview.DataRow label="Require explicit user specified values">
                {definition.requireExplicitValues ? 'Enabled' : 'Disabled'}
              </DefinitionReadOnlyOverview.DataRow>
            )}
            <DefinitionReadOnlyOverview.DataRow label="Allow repository actors to set this property">
              {definition.valuesEditableBy === 'org_and_repo_actors' ? 'Enabled' : 'Disabled'}
            </DefinitionReadOnlyOverview.DataRow>
          </DefinitionReadOnlyOverview>
          {showDangerZone && (
            <div className="pr-mt-4">
              <DefinitionDangerZone
                business={business}
                definition={definition}
                orgConflicts={orgConflicts}
                canDelete={canDeleteProperty}
                canPromote={canPromoteProperty}
              />
            </div>
          )}
        </div>
      ) : (
        <DefinitionForm
          definition={definition}
          existingPropertyNames={propertyNames}
          onCancel={onCancel}
          onSuccess={onSuccess}
          setFormError={setFormError}
          propertiesLimits={propertiesLimits}
        />
      )}
    </>
  )
}
